import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const { user, changePassword, changeUsername, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('password'); // 'password' or 'username'
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Username change state
  const [usernameData, setUsernameData] = useState({
    newUsername: '',
    password: ''
  });
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setPasswordLoading(true);
    try {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (result.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordError(result.error || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError(error.message || 'An error occurred');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    setUsernameError('');
    setUsernameSuccess('');

    // Validation
    if (!usernameData.newUsername || !usernameData.password) {
      setUsernameError('All fields are required');
      return;
    }

    if (usernameData.newUsername.trim().length < 3) {
      setUsernameError('Username must be at least 3 characters long');
      return;
    }

    if (usernameData.newUsername.trim() === user?.username) {
      setUsernameError('New username must be different from current username');
      return;
    }

    setUsernameLoading(true);
    try {
      const result = await changeUsername(usernameData.newUsername.trim(), usernameData.password);
      if (result.success) {
        setUsernameSuccess('Username changed successfully!');
        setUsernameData({
          newUsername: '',
          password: ''
        });
        // Update will be reflected in the context automatically
      } else {
        setUsernameError(result.error || 'Failed to change username');
      }
    } catch (error) {
      setUsernameError(error.message || 'An error occurred');
    } finally {
      setUsernameLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="settings-title">Settings</h1>
        <p className="settings-subtitle">Manage your account settings</p>
        {user && (
          <p className="settings-user-info">
            Logged in as: <strong>{user.username}</strong>
          </p>
        )}
      </div>

      <div className="settings-content">
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('password');
              setPasswordError('');
              setPasswordSuccess('');
            }}
          >
            Change Password
          </button>
          <button
            className={`settings-tab ${activeTab === 'username' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('username');
              setUsernameError('');
              setUsernameSuccess('');
            }}
          >
            Change Username
          </button>
        </div>

        <div className="settings-panel">
          {activeTab === 'password' && (
            <div className="settings-form-container">
              <h2 className="settings-form-title">Change Password</h2>
              <form onSubmit={handlePasswordChange} className="settings-form">
                <div className="form-group">
                  <label htmlFor="current-password">Current Password</label>
                  <input
                    id="current-password"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Enter current password"
                    className="form-input"
                    disabled={passwordLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <input
                    id="new-password"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Enter new password (min. 6 characters)"
                    className="form-input"
                    disabled={passwordLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm New Password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    className="form-input"
                    disabled={passwordLoading}
                  />
                </div>

                {passwordError && (
                  <div className="alert alert-error">
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="alert alert-success">
                    {passwordSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'username' && (
            <div className="settings-form-container">
              <h2 className="settings-form-title">Change Username</h2>
              <p className="settings-form-description">
                Current username: <strong>{user?.username}</strong>
              </p>
              <form onSubmit={handleUsernameChange} className="settings-form">
                <div className="form-group">
                  <label htmlFor="new-username">New Username</label>
                  <input
                    id="new-username"
                    type="text"
                    value={usernameData.newUsername}
                    onChange={(e) => setUsernameData({ ...usernameData, newUsername: e.target.value })}
                    placeholder="Enter new username (min. 3 characters)"
                    className="form-input"
                    disabled={usernameLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="username-password">Password</label>
                  <input
                    id="username-password"
                    type="password"
                    value={usernameData.password}
                    onChange={(e) => setUsernameData({ ...usernameData, password: e.target.value })}
                    placeholder="Enter your password to confirm"
                    className="form-input"
                    disabled={usernameLoading}
                  />
                </div>

                {usernameError && (
                  <div className="alert alert-error">
                    {usernameError}
                  </div>
                )}

                {usernameSuccess && (
                  <div className="alert alert-success">
                    {usernameSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={usernameLoading}
                >
                  {usernameLoading ? 'Changing...' : 'Change Username'}
                </button>
              </form>
            </div>
          )}
        </div>
        <div className="settings-footer">
          <button
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="btn btn-logout"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;

